import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/data";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-stone-50/85 border-b border-stone-200"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        <Link
          to="/"
          data-testid="header-logo"
          className="font-display font-bold text-xl lg:text-2xl tracking-tight text-neutral-900 hover:text-amber-700 transition-colors"
        >
          {COMPANY.name}
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-neutral-900"
                    : "text-neutral-600 hover:text-neutral-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <span className="absolute left-4 right-4 -bottom-0.5 h-[2px] bg-amber-700" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={COMPANY.phoneHref}
            data-testid="header-phone-link"
            className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-amber-700 transition-colors px-3 py-2"
          >
            <Phone size={14} />
            <span className="hu-ticker">{COMPANY.phone}</span>
          </a>
          <Link
            to="/quote"
            data-testid="header-quote-cta"
            className="hidden sm:inline-flex items-center bg-amber-700 hover:bg-amber-800 text-white text-sm font-medium px-5 py-2.5 rounded-sm transition-colors"
          >
            Request a Quote
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center border border-stone-300 rounded-sm"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stone-200 bg-stone-50">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2.5 text-sm font-medium ${
                    isActive
                      ? "text-amber-700 bg-stone-100"
                      : "text-neutral-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/quote"
              data-testid="mobile-quote-cta"
              className="mt-2 bg-amber-700 text-white text-sm font-medium px-4 py-2.5 rounded-sm text-center"
            >
              Request a Quote
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-testid="mobile-phone-link"
              className="mt-1 inline-flex items-center justify-center gap-2 border border-stone-300 text-neutral-800 text-sm font-medium px-4 py-2.5 rounded-sm"
            >
              <Phone size={14} /> <span className="hu-ticker">{COMPANY.phone}</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
